import * as React from "react";
import { PlayersServices } from "@/services/bedrock/PlayersServices";
import { SocialNetworksId } from "@/interfaces";
import { SocialMentionData, SocialMentionSearchResult } from "@/services/bedrock/dtos/Post";
import { useSocialCoachSession } from "@/utils";
import { useQuery } from "@tanstack/react-query";
import { AbstractPlayer } from "../index";

interface UseSocialMentionSearchProps {
  players: AbstractPlayer[];
  mentions: SocialMentionData[];
  onAddMention: (mention: SocialMentionData) => void;
  onRemoveMention: (mention: SocialMentionData) => void;
}

export function useSocialMentionSearch({
  players,
  mentions,
  onAddMention,
  onRemoveMention,
}: UseSocialMentionSearchProps) {
  const { isPlayer, playerId: sessionPlayerId, userId: sessionUserId } = useSocialCoachSession();

  const [isSearching, setIsSearching] = React.useState(false);
  const [selectedNetwork, setSelectedNetwork] = React.useState<SocialNetworksId | undefined>(undefined);
  const [selectedPlayer, setSelectedPlayer] = React.useState<AbstractPlayer | null>(
    players.length > 0 ? players[0] : null
  );

  const effectiveUserId = players.length > 0 ? selectedPlayer?.userId : isPlayer ? sessionUserId : undefined;
  const effectivePlayerId = players.length > 0 ? selectedPlayer?.playerId : Number(sessionPlayerId);

  const [searchResults, setSearchResults] = React.useState<SocialMentionData[]>([]);
  const [searchError, setSearchError] = React.useState<string | null>(null);

  const { data: previousMentionsPage } = useQuery({
    queryKey: ["previousMentions", effectivePlayerId, selectedNetwork],
    queryFn: () => PlayersServices.getPreviousSocialMentions(Number(effectivePlayerId), selectedNetwork!),
    enabled: !!effectivePlayerId && !!selectedNetwork,
  });

  const previousMentions = React.useMemo(() => previousMentionsPage?.content || [], [previousMentionsPage?.content]);

  const handleSearch = async (query: string) => {
    if (!effectivePlayerId || !selectedNetwork || !query.trim()) return;

    setIsSearching(true);
    setSearchError(null);
    try {
      const results = await PlayersServices.searchSocialMentions(effectivePlayerId, {
        socialNetworkId: selectedNetwork,
        query: query.trim(),
      });
      setSearchResults(results);
      if (results.length === 0) {
        setSearchError("No profiles found. Please enter the exact name or handle.");
      }
    } catch (e) {
      setSearchError("Failed to search for mentions. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleAdd = (result: SocialMentionSearchResult) => {
    if (!effectivePlayerId) return;

    onAddMention({
      ...result,
      playerId: effectivePlayerId,
    });
  };

  const handleBulkAdd = (result: SocialMentionSearchResult) => {
    // Check for any existing individual mentions for this profile and remove them first
    const existing = mentions.filter(
      m => m.socialNetworkId === result.socialNetworkId && m.externalId === result.externalId
    );

    existing.forEach(m => {
      onRemoveMention(m);
    });

    onAddMention({
      ...result,
      playerId: undefined,
    });
  };

  const openPublicLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const clearError = () => setSearchError(null);

  return {
    // State
    players,
    isSearching,
    selectedNetwork,
    setSelectedNetwork,
    selectedPlayer,
    setSelectedPlayer,
    searchResults,
    searchError,
    previousMentions,
    effectiveUserId,
    effectivePlayerId,
    // Handlers
    handleSearch,
    handleAdd,
    handleBulkAdd,
    openPublicLink,
    clearError,
    setSearchResults,
    setSearchError,
    setIsSearching,
  };
}

export type SocialMentionSearchHook = ReturnType<typeof useSocialMentionSearch>;
